import { useEffect, useState } from 'react';
import { Alert, Button, Group, Select, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import { FormModal } from '../../components/ui/FormModal';
import { plantsApi } from '../../services/plantsApi';
import { getApiErrorMessage, getApiViolations } from '../../utils/errorHandling';
import { mapViolations } from '../../utils/mapViolations';
import { Region, RegionLabels } from '../../types/entities';

interface CreatePlantModalProps {
  opened: boolean;
  onClose: () => void;
  companyId: number;
  /** Laeuft nach dem POST und vor dem Schliessen -- der Aufrufer laedt hier neu und springt in
   * das neue Werk. */
  onCreated: (plantId: number) => Promise<void>;
}

/**
 * Anlegen-Dialog fuer ein Werk. Ersetzt das frueher sofortige POST mit Platzhaltern ("Neues Werk
 * N", Region still auf Nord-West): `plant.region` ist NOT NULL mit `Assert\NotNull`
 * (Plant.php:60), also muss beim Anlegen ohnehin eine Region mitgehen -- die soll der Nutzer
 * waehlen, statt sie geraten zu bekommen. Eine falsche Region sieht man einem Werk spaeter nicht
 * an, einen Platzhalternamen schon.
 */
export function CreatePlantModal({ opened, onClose, companyId, onCreated }: CreatePlantModalProps) {
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const form = useForm<{ name: string; region: Region | null }>({
    mode: 'controlled',
    initialValues: { name: '', region: null },
  });

  // Beim Oeffnen leere Felder -- sonst stuende beim zweiten Anlegen noch der vorige Stand da.
  useEffect(() => {
    if (opened) {
      form.reset();
      setError(null);
    }
    // form ist ueber die Lebenszeit der Komponente stabil; nur das Oeffnen soll zuruecksetzen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  async function handleSubmit(values: { name: string; region: Region | null }) {
    if (!values.region) {
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const created = await plantsApi.create({
        name: values.name.trim(),
        company: `/api/companies/${companyId}`,
        region: values.region,
      });
      await onCreated(created.id);
      onClose();
    } catch (err) {
      form.setErrors(mapViolations(getApiViolations(err)));
      setError(getApiErrorMessage(err, 'Fehler beim Anlegen des Werks'));
    } finally {
      setSaving(false);
    }
  }

  const values = form.getValues();

  return (
    <FormModal opened={opened} onClose={onClose} title="Werk anlegen">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red">
              {error}
            </Alert>
          )}
          <TextInput
            label="Name"
            placeholder="Werksname"
            data-autofocus
            required
            {...form.getInputProps('name')}
          />
          <Select
            label="Region"
            placeholder="— bitte wählen —"
            data={Object.values(Region).map((value) => ({ value, label: RegionLabels[value] }))}
            required
            {...form.getInputProps('region')}
            onChange={(value) => form.setFieldValue('region', (value as Region) || null)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={!values.name.trim() || !values.region} loading={saving}>
              Werk anlegen
            </Button>
          </Group>
        </Stack>
      </form>
    </FormModal>
  );
}
